/* eslint-disable no-param-reassign */
import React, { useCallback, useEffect, useRef, useState } from "react";
import Webcam from "react-webcam";
import * as ort from "onnxruntime-web";
import { Hands, Results, HAND_CONNECTIONS } from "@mediapipe/hands";
import { drawConnectors, drawLandmarks } from "@mediapipe/drawing_utils";

import cameraIcon from "../../assets/icons/camera-icon.svg";
import closeIcon from "../../assets/icons/close-icon.svg";

import { Container, WebcamContainer } from "./styles";

if (typeof window !== 'undefined') {
  (ort.env.wasm.wasmPaths as any) = "https://cdn.jsdelivr.net/npm/onnxruntime-web@1.23.2/dist/";
}

const ONNX_MODEL_FILE_PATH = "./model/modelo_libras_numeros.onnx";
const ONNX_METADATA_FILE_PATH = "./model/modelo_metadata.json";

const VELOCIDADE_THRESHOLD = 0.015;
const MIN_FRAMES_SEGMENTO = 2;
const GAP_MAXIMO_S_DIGITO = 0.2; 
const FRAMES_SEM_MAO_LIMITE = 8;

interface LibrasReplyPopupProps {
  active?: boolean;
}

export function LibrasReplyPopup( { active }: LibrasReplyPopupProps) {
  const webRef = useRef<Webcam>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const handsRef = useRef<Hands | null>(null);
  const sessionRef = useRef<ort.InferenceSession | null>(null);
  const metadataRef = useRef<any>(null);
  const isCameraOpenRef = useRef(false);

  const historico = useRef<any[]>([]);
  const landmarksAnterior = useRef<any[] | null>(null);
  const framesSemMao = useRef(0);
  const processando = useRef(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const [cameraOpen, setCameraOpen] = useState(false);
  const [resultadoFinal, setResultadoFinal] = useState<string | null>(null);

  const [position, setPosition] = useState({ x: 50, y: 50 });
  const [isDragging, setIsDragging] = useState(false);
  const dragOffset = useRef({ x: 0, y: 0 });
  const moved = useRef(false);

  const resolution = { width: 640, height: 480 };
  const [videoSize, setVideoSize] = useState({ width: resolution.width, height: resolution.height });
  const containerRef = useRef<HTMLDivElement>(null);
  
  const normalizeLandmarks = (landmarks: any) => {
    const coords: number[] = [];
    for (let i = 0; i < 21; i++) coords.push(landmarks[i].x, landmarks[i].y);

    const wristX = coords[0];
    const wristY = coords[1];

    const refX = coords[18] - wristX; 
    const refY = coords[19] - wristY;
    const refDist = Math.sqrt(refX * refX + refY * refY) + 1e-6;

    return coords.map((val, i) => (val - (i % 2 === 0 ? wristX : wristY)) / refDist);
  };

  const extrairDuasMaos = (multiHandLandmarks: any[], multiHandedness: any[]) => {
    let pontosEsq = new Array(42).fill(0);
    let pontosDir = new Array(42).fill(0);

    if (!multiHandLandmarks || multiHandLandmarks.length === 0) return null;

    multiHandLandmarks.forEach((landmarks, i) => {
      const label = multiHandedness[i].label;
      const pontos = normalizeLandmarks(landmarks);
      if (label === 'Left') pontosDir = pontos;
      else pontosEsq = pontos;
    });

    return { pontosEsq, pontosDir };
  };

  const predictLocal = async (dadosMaos: { pontosEsq: number[], pontosDir: number[] }) => {
    if (!sessionRef.current || !metadataRef.current) return null;
    try {
      const { input_names, output_name, hand_dim, classes, labels_dict } = metadataRef.current;

      const tensorEsq = new ort.Tensor("float32", new Float32Array(dadosMaos.pontosEsq), [1, hand_dim]);
      const tensorDir = new ort.Tensor("float32", new Float32Array(dadosMaos.pontosDir), [1, hand_dim]);

      const feeds: any = {};
      feeds[input_names[0]] = tensorEsq;
      feeds[input_names[1]] = tensorDir;

      const results = await sessionRef.current.run(feeds);
      const logits = results[output_name].data as Float32Array;
      
      const maxLogit = Math.max(...Array.from(logits));
      const probs = Array.from(logits).map(l => Math.exp(l - maxLogit));
      const sum = probs.reduce((a, b) => a + b, 0);
      const finalProbs = probs.map(p => p / sum);
      
      const maxP = Math.max(...finalProbs);
      const predictedIndex = finalProbs.indexOf(maxP);
      const classeBruta = classes[predictedIndex];
      const resFinal = labels_dict ? (labels_dict[classeBruta] || classeBruta) : classeBruta;


      return { numero: String(resFinal).split("_")[0], confianca: maxP };
    } catch (e) { return null; }
  };

  const classificarSinal = async () => {
    if (historico.current.length < 3 || processando.current) {
      historico.current = [];
      return;
    }
    processando.current = true;
    try {
      const framesClassificados = [];
      for (const f of historico.current) {
        const res = await predictLocal({ pontosEsq: f.pontosEsq, pontosDir: f.pontosDir });
        if (res && res.confianca > 0.75) framesClassificados.push({ ...res, t: f.t });
      }

      const segmentos: string[] = [];
      if (framesClassificados.length > 0) {
        let atual = [framesClassificados[0]];
        for (let i = 1; i < framesClassificados.length; i++) {
          const dt = (framesClassificados[i].t - framesClassificados[i-1].t) / 1000;
          if (framesClassificados[i].numero === atual[0].numero && dt < GAP_MAXIMO_S_DIGITO) {
            atual.push(framesClassificados[i]);
          } else {
            if (atual.length >= MIN_FRAMES_SEGMENTO) segmentos.push(atual[0].numero);
            atual = [framesClassificados[i]];
          }
        }
        if (atual.length >= MIN_FRAMES_SEGMENTO) segmentos.push(atual[0].numero);
      }

      if (segmentos.length > 0) {
        const resultado = segmentos.includes("voltar") ? "voltar" : segmentos.includes("pausar") ? "pausar" : segmentos.join('');
        setResultadoFinal(resultado);
        window.dispatchEvent(new CustomEvent("LIBRAS_SELECTION", { detail: { number: resultado } }));
      }
    } finally {
      historico.current = [];
      processando.current = false;
      framesSemMao.current = 0;
    }
  };

  const onResults = useCallback(async (results: Results) => {
    const canvasCtx = canvasRef.current?.getContext("2d");
    if (!canvasCtx || !canvasRef.current) return;

    canvasCtx.save();
    canvasCtx.translate(canvasRef.current.width, 0);
    canvasCtx.scale(-1, 1);
    canvasCtx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    canvasCtx.drawImage(results.image, 0, 0, canvasRef.current.width, canvasRef.current.height);

    const temMaos = results.multiHandLandmarks && results.multiHandLandmarks.length > 0;

    if (temMaos) {
      results.multiHandLandmarks.forEach((landmarks) => {
        if (process.env.REACT_APP_SHOW_HAND_LANDMARKS === 'true') {
          drawConnectors(canvasCtx, landmarks, HAND_CONNECTIONS, { color: "#00FF00", lineWidth: 2 });
          drawLandmarks(canvasCtx, landmarks, { color: "#FF0000", lineWidth: 1, radius: 2 });
        }
      });

      if (isCameraOpenRef.current && !processando.current) {
        let v = 0;
        if (landmarksAnterior.current && landmarksAnterior.current.length > 0) {
          const numMaos = Math.min(landmarksAnterior.current.length, results.multiHandLandmarks.length);
          let velAcumulada = 0;
        for (let h = 0; h < numMaos; h++) {
          const mAtual = results.multiHandLandmarks[h];
          const mAnt = landmarksAnterior.current[h];
          velAcumulada += mAtual.reduce((acc, curr, i) =>
            acc + Math.sqrt(Math.pow(curr.x - mAnt[i].x, 2) + Math.pow(curr.y - mAnt[i].y, 2)), 0) / 21;
        }
        v = velAcumulada / numMaos;
      }

      landmarksAnterior.current = results.multiHandLandmarks;
      const dadosMaos = extrairDuasMaos(results.multiHandLandmarks, results.multiHandedness);

      if (dadosMaos && v < VELOCIDADE_THRESHOLD) {
        historico.current.push({ ...dadosMaos, t: Date.now() });
      }
      framesSemMao.current = 0;
    }
    } else if (isCameraOpenRef.current && historico.current.length > 0) {
      framesSemMao.current++;
      if (framesSemMao.current >= FRAMES_SEM_MAO_LIMITE) await classificarSinal();
    }
    canvasCtx.restore();
  }, []);

  useEffect(() => {
    isCameraOpenRef.current = cameraOpen;
  }, [cameraOpen]);

  useEffect(() => {
    isCameraOpenRef.current = cameraOpen;
  }, [cameraOpen]);

  useEffect(() => {
    if (!cameraOpen) return;

    async function initAiModels() {
      if (!sessionRef.current) {
      try {
        const metaResponse = await fetch(ONNX_METADATA_FILE_PATH);
        metadataRef.current = await metaResponse.json();
        sessionRef.current = await ort.InferenceSession.create(ONNX_MODEL_FILE_PATH as any, {
          executionProviders: ["wasm"],
          graphOptimizationLevel: "all",
        });
      } catch (err) {
        console.error(err);
      }
    }

    if (!handsRef.current) {
      const hands = new Hands({
        locateFile: (f) => `https://cdn.jsdelivr.net/npm/@mediapipe/hands/${f}`,
      });
      hands.setOptions({
        maxNumHands: 2,
        modelComplexity: 0,
        minDetectionConfidence: 0.7,
        minTrackingConfidence: 0.5,
      });
      hands.onResults(onResults);
      handsRef.current = hands;
    }
  }

  initAiModels();
}, [cameraOpen, onResults]);

  const runDetection = useCallback(async () => {
    if (cameraOpen && webRef.current?.video?.readyState === 4 && handsRef.current) {
      await handsRef.current.send({ image: webRef.current.video });
    }
    if (cameraOpen) timeoutRef.current = setTimeout(() => requestAnimationFrame(runDetection), 45);
  }, [cameraOpen]);

  useEffect(() => {
    if (cameraOpen) runDetection();
    return () => { if (timeoutRef.current) clearTimeout(timeoutRef.current); };
  }, [cameraOpen, runDetection]);

  useEffect(() => {
    const handleResize = () => {
      const isPortrait = window.innerHeight > window.innerWidth;
      setVideoSize(isPortrait ? { width: resolution.height, height: resolution.width } : { width: resolution.width, height: resolution.height });
      
      if (containerRef.current) {
        const { width, height } = containerRef.current.getBoundingClientRect();
        setPosition(prev => ({
          x: Math.min(prev.x, window.innerWidth - width - 10),
          y: Math.min(prev.y, window.innerHeight - height - 10)
        }));
      }
    };
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const stopCamera = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (webRef.current?.video) { (webRef.current.video.srcObject as MediaStream)?.getTracks().forEach(t => t.stop()); }
    setCameraOpen(false);
    setResultadoFinal(null);
    historico.current = [];
    landmarksAnterior.current = null;
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    moved.current = false;
    setIsDragging(true);
    dragOffset.current = { x: e.clientX - position.x, y: e.clientY - position.y };
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    moved.current = false;
    setIsDragging(true);
    const touch = e.touches[0];
    dragOffset.current = { x: touch.clientX - position.x, y: touch.clientY - position.y };
  };

  useEffect(() => {
    const handleMove = (e: MouseEvent | TouchEvent) => {
      if (!isDragging || !containerRef.current) return;
      const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
      const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;

      if (Math.abs(clientX - (position.x + dragOffset.current.x)) > 5 || Math.abs(clientY - (position.y + dragOffset.current.y)) > 5) {
        moved.current = true;
      }

      let newX = clientX - dragOffset.current.x;
      let newY = clientY - dragOffset.current.y;

      const { width, height } = containerRef.current.getBoundingClientRect();
      newX = Math.max(10, Math.min(newX, window.innerWidth - width - 10));
      newY = Math.max(10, Math.min(newY, window.innerHeight - height - 10));

      setPosition({ x: newX, y: newY });
    };

    const handleEnd = () => setIsDragging(false);
    if (isDragging) {
      window.addEventListener("mousemove", handleMove);
      window.addEventListener("mouseup", handleEnd);
      window.addEventListener("touchmove", handleMove, { passive: false });
      window.addEventListener("touchend", handleEnd);
    }
    return () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleEnd);
      window.removeEventListener("touchmove", handleMove);
      window.removeEventListener("touchend", handleEnd);
    };
  }, [isDragging, position]);

  return (
    active? (
    <Container
      ref={containerRef}
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
      onKeyDown={(e) => {
        if ((e.key === "Enter" || e.key === " ") && !cameraOpen) {
          e.preventDefault();
          setCameraOpen(true);
        }
      }}
      tabIndex={11}
      style={{
        position: "fixed",
        left: `${position.x}px`,
        top: `${position.y}px`,
        cursor: isDragging ? "grabbing" : "grab",
        zIndex: 10005,
        touchAction: "none"
      }}
    >
      {!cameraOpen ? (
        <div 
          className="camera-button hint"
          data-tutorial="camera-button"
          data-hint="Abrir câmera"
          data-hint-placement="bottom-left"
          onClick={(e) => { if (!moved.current) setCameraOpen(true); }}
        >
          <img src={cameraIcon} alt="Camera" draggable="false" style={{ pointerEvents: 'none' }} />
        </div>
      ) : (
        <WebcamContainer
          style={{
            width: videoSize.width === resolution.height ? "120px" : "176px",
            height: videoSize.height === resolution.width ? "160px" : "120px",
          }}
        >
          <span className="close-button" onClick={stopCamera} onMouseDown={(e) => e.stopPropagation()} onTouchStart={(e) => e.stopPropagation()} tabIndex={12} onKeyDown={(e) => {if ((e.key === "Enter" || e.key === " ") && cameraOpen) { e.preventDefault(); stopCamera(); }}}>
            <img src={closeIcon} alt="Fechar" />
          </span>
          <Webcam
            ref={webRef}
            audio={false}
            mirrored
            videoConstraints={{ width: resolution.width, height: resolution.height, facingMode: "user" }}
            style={{ opacity: 0, position: "absolute", width: "1px", height: "1px" }} 
          />
          <canvas
            ref={canvasRef}
            width={videoSize.width}
            height={videoSize.height}
            className="camera-image"
            style={{ width: "100%", height: "100%" }} 
          />
          {resultadoFinal && (
            <div style={{ position: "absolute", bottom: 5, width: "100%", textAlign: "center", background: "rgba(0,128,0,0.8)", color: "#fff", fontSize: "14px", fontWeight: "bold", pointerEvents: 'none' }} >
              {resultadoFinal}
            </div>
          )}
        </WebcamContainer>
      )}
    </Container>
    ) : null
  );
}